import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

// Tạo instance axios với cấu hình mặc định
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Thêm interceptor để xử lý token
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    if (user.token) {
      config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class PlatformScheduleService {
  // Get user's role information
  getUserRole() {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    return user.role || 'user';
  }

  // Check if user can access platform
  canAccessPlatform(platform) {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const role = user.role;
    
    if (role === 'admin') return true;
    if (role === 'live_multi') return user.allowedPlatforms?.includes(platform);
    if (role === `live_${platform}`) return true;
    return false;
  }

  // Get user's accessible platforms
  getUserPlatforms() {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const role = user.role;
    
    if (role === 'admin') return ['shopee', 'tiktok'];
    if (role === 'live_multi') return user.allowedPlatforms || [];
    if (role === 'live_shopee') return ['shopee'];
    if (role === 'live_tiktok') return ['tiktok'];
    return [];
  }

  // Create schedule (auto-detect platform từ user role)
  async createSchedule(scheduleData) {
    try {
      const response = await api.post('/platform-schedule/', scheduleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Create schedule for specific platform
  async createScheduleForPlatform(platform, scheduleData) {
    try {
      const response = await api.post(`/platform-schedule/${platform}`, scheduleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get user's schedules (all accessible platforms)
  async getUserSchedules(userId, startDate = null, endDate = null, status = null) {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (status) params.status = status;

      const response = await api.get(`/platform-schedule/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get schedules for specific date
  async getSchedulesForDate(date, platform = null) {
    try {
      const params = platform ? { platform } : {};
      const response = await api.get(`/platform-schedule/date/${date}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Update schedule
  async updateSchedule(platform, scheduleId, updateData) {
    try {
      const response = await api.put(`/platform-schedule/${platform}/${scheduleId}`, updateData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Delete schedule
  async deleteSchedule(platform, scheduleId) {
    try {
      const response = await api.delete(`/platform-schedule/${platform}/${scheduleId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Check time conflicts
  async checkConflicts(date, startTime, endTime, excludeScheduleId = null, excludePlatform = null) {
    try {
      const response = await api.post('/platform-schedule/check-conflicts', {
        date,
        startTime,
        endTime,
        excludeScheduleId,
        excludePlatform
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get available slots for platform
  async getAvailableSlots(platform, date, userId = null) {
    try {
      const params = userId ? { userId } : {};
      const response = await api.get(`/platform-schedule/slots/${platform}/${date}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get platform statistics (admin only)
  async getPlatformStats(platform, startDate = null, endDate = null) {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get(`/platform-schedule/stats/${platform}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Batch operations (admin only)
  async batchOperation(platform, scheduleIds, action) {
    try {
      const response = await api.post(`/platform-schedule/batch/${platform}`, {
        scheduleIds,
        action
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get cross-platform analytics
  async getCrossPlatformAnalytics(userId, startDate = null, endDate = null) {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get(`/platform-schedule/analytics/${userId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Platform-specific features

  // Generate Shopee stream key
  async generateShopeeStreamKey(scheduleId) {
    try {
      const response = await api.post(`/platform-schedule/shopee/${scheduleId}/stream-key`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Generate TikTok live ID
  async generateTikTokLiveId(scheduleId) {
    try {
      const response = await api.post(`/platform-schedule/tiktok/${scheduleId}/live-id`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Helper methods

  // Format schedules từ API response (handle platform-specific data)
  formatSchedules(apiResponse) {
    if (!apiResponse.success || !apiResponse.data) return [];

    // If data is platform-separated object
    if (typeof apiResponse.data === 'object' && !Array.isArray(apiResponse.data)) {
      const allSchedules = [];
      Object.keys(apiResponse.data).forEach(platform => {
        const platformSchedules = apiResponse.data[platform] || [];
        platformSchedules.forEach(schedule => {
          allSchedules.push({
            ...schedule,
            platform,
            _formattedPlatform: this.getPlatformDisplayName(platform)
          });
        });
      });
      return allSchedules;
    }

    // If data is array (single platform or merged)
    return apiResponse.data.map(schedule => ({
      ...schedule,
      _formattedPlatform: schedule.platform ? this.getPlatformDisplayName(schedule.platform) : 'Unknown'
    }));
  }

  // Get platform display info
  getPlatformDisplayName(platform) {
    switch (platform) {
      case 'shopee': return 'Shopee';
      case 'tiktok': return 'TikTok';
      default: return platform;
    }
  }

  getPlatformColor(platform) {
    switch (platform) {
      case 'shopee': return '#EE4D2D';
      case 'tiktok': return '#000000';
      default: return '#666666';
    }
  }

  getPlatformIcon(platform) {
    switch (platform) {
      case 'shopee': return '🛒';
      case 'tiktok': return '🎵';
      default: return '📱';
    }
  }

  // Detect platform from schedule data
  detectPlatformFromSchedule(schedule) {
    // Check for platform-specific fields
    if (schedule.shopeeStreamKey || schedule.expectedProducts) return 'shopee';
    if (schedule.tiktokLiveId || schedule.hashtags || schedule.contentType) return 'tiktok';
    
    // Fallback to explicit platform field
    return schedule.platform || 'unknown';
  }

  // DEPRECATED: Fallback methods for backward compatibility
  async createScheduleOld(scheduleData) {
    console.warn('⚠️ Using deprecated createSchedule method. Use createSchedule() instead.');
    try {
      const response = await api.post('/schedule/', scheduleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getUserSchedulesOld(userId, startDate = null, endDate = null, status = null) {
    console.warn('⚠️ Using deprecated getUserSchedules method. Use getUserSchedules() instead.');
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (status) params.status = status;

      const response = await api.get(`/schedule/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new PlatformScheduleService(); 